import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Ruler, ShieldCheck, ClipboardList, CalendarClock, Phone, Mail, ClipboardEdit, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { SITE } from "@/lib/site";

const HERO_IMG = "https://images.unsplash.com/photo-1581578017093-cd30fce4eeb7?auto=format&fit=crop&w=2000&q=80";

const FACTORS = [
  { icon: Ruler, title: "Property Size", text: "Square footage is the primary factor in our pricing calculation." },
  { icon: ShieldCheck, title: "Condition at Time of Service", text: "Properties requiring more intensive attention may carry a higher initial rate." },
  { icon: ClipboardList, title: "Scope of Work", text: "Standard cleaning, deep cleaning, move-in/move-out, and specialty services are priced accordingly." },
  { icon: CalendarClock, title: "Frequency", text: "Recurring clients benefit from consistent scheduling and preferred pricing." },
];

export default function Pricing() {
  useEffect(() => { document.title = "Pricing | Eco Ready Solutions, LLC"; }, []);

  return (
    <div data-testid="page-pricing">
      <PageHero
        eyebrow="Pricing"
        title="Professional Cleaning. Honest Pricing."
        subtitle="Transparent rates. No hidden fees. Built around your actual property."
        image={HERO_IMG}
      />

      {/* HOW PRICING WORKS */}
      <section className="eco-section bg-white">
        <div className="eco-container max-w-4xl">
          <span className="eco-eyebrow">Approach</span>
          <h2 className="eco-h2 mt-3">How Our Pricing Works</h2>
          <p className="mt-6 text-[17px] leading-[1.8] text-[#1A1A1A]">
            At Eco Ready Solutions, LLC, we price our services based on the real scope of work — not flat rates that
            reward shortcuts. Our standard pricing begins at <strong>$0.12 per square foot</strong>, with final rates
            determined by the property&apos;s size, current condition, cleaning frequency, and the specific services
            required. Whether you need a one-time deep clean or a recurring commercial program, we will build a price
            that reflects the actual work and delivers genuine value.
          </p>
        </div>
      </section>

      {/* FACTORS */}
      <section className="eco-section" style={{ background: "#F4F6F5" }}>
        <div className="eco-container">
          <div className="max-w-2xl">
            <span className="eco-eyebrow">What Goes Into A Quote</span>
            <h2 className="eco-h2 mt-3">What Determines Your Rate</h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FACTORS.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} data-testid={`factor-${i}`} className="eco-card">
                  <span className="eco-icon-wrap"><Icon size={22} /></span>
                  <h3 className="text-[17px] font-semibold mt-5">{f.title}</h3>
                  <p className="mt-2 text-[#555555] text-[14.5px] leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMMERCIAL */}
      <section className="eco-section bg-white">
        <div className="eco-container grid lg:grid-cols-[1.3fr_1fr] gap-10">
          <div>
            <span className="eco-eyebrow">For Businesses</span>
            <h2 className="eco-h2 mt-3">Commercial Pricing</h2>
            <p className="mt-6 text-[17px] leading-[1.8] text-[#1A1A1A]">
              For commercial clients, we provide custom quotes based on a walkthrough or detailed property assessment.
              We understand that businesses are often working within a budget — and we are transparent, fair, and
              flexible in our commercial pricing. If you are currently paying for professional cleaning services, we
              invite you to share your current rate. We are often able to meet or improve upon it while delivering a
              higher level of consistency and professionalism.
            </p>
          </div>
          <div className="self-center">
            <div className="rounded-[14px] p-7"
              style={{ background: "#F4F6F5", borderLeft: "4px solid #2D6A4F" }}>
              <p className="text-[13px] uppercase tracking-[0.18em] text-[#1B7A6E] font-semibold">Commercial</p>
              <p className="text-3xl font-extrabold mt-3 text-[#1A1A1A]">Starting at $0.12<span className="text-base font-medium text-[#555555]">/sq ft</span></p>
              <p className="mt-3 text-[15px] text-[#555555] leading-relaxed">
                Custom commercial quotes available. <br/>We welcome rate comparisons.
              </p>
              <Link to="/contact" className="eco-btn eco-btn-primary mt-6">Request a Walkthrough</Link>
            </div>
          </div>
        </div>
      </section>

      {/* RESIDENTIAL */}
      <section className="eco-section" style={{ background: "#F4F6F5" }}>
        <div className="eco-container grid lg:grid-cols-[1.3fr_1fr] gap-10">
          <div>
            <span className="eco-eyebrow">For Homes</span>
            <h2 className="eco-h2 mt-3">Residential Pricing</h2>
            <p className="mt-6 text-[17px] leading-[1.8] text-[#1A1A1A]">
              Residential pricing is based on the size of the home, the type of service requested (standard, deep
              clean, move-in/move-out), and how often service is scheduled. We do not believe in pricing surprises.
              Before any service begins, you will know exactly what to expect.
            </p>
          </div>
          <div className="self-center">
            <div className="rounded-[14px] p-7 bg-white"
              style={{ border: "1px solid #D6DDD9", borderLeft: "4px solid #1B7A6E" }}>
              <p className="text-[13px] uppercase tracking-[0.18em] text-[#1B7A6E] font-semibold">Residential</p>
              <p className="text-3xl font-extrabold mt-3 text-[#1A1A1A]">Quoted by Home</p>
              <p className="mt-3 text-[15px] text-[#555555] leading-relaxed">
                Pricing based on size, scope, and frequency.<br/>Free estimates · No surprises.
              </p>
              <Link to="/contact" className="eco-btn eco-btn-ghost mt-6">Get My Home Quote</Link>
            </div>
          </div>
        </div>
      </section>

      {/* RATE MATCHING */}
      <section className="eco-section text-white" style={{ background: "#2D6A4F" }}>
        <div className="eco-container grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80">Rate Matching</span>
            <h2 className="eco-h2 text-white mt-3">We&apos;re Committed to Earning Your Business</h2>
            <p className="mt-5 text-white/90 text-[17px] leading-[1.8] max-w-2xl">
              If you are currently working with another cleaning provider and you are comfortable sharing your current
              rate, we would be glad to review it and explore whether we can offer a more competitive arrangement. Our
              goal is not simply to be the cheapest option — it is to be the best value.
            </p>
          </div>
          <div className="lg:justify-self-end">
            <Link to="/contact" data-testid="pricing-cta-rate" className="eco-btn"
              style={{ background: "#fff", color: "#2D6A4F" }}>
              Tell Us Your Current Rate <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* ESTIMATE CTA */}
      <section className="eco-section bg-white">
        <div className="eco-container max-w-3xl mx-auto text-center">
          <h2 className="eco-h2">Get Your Free, No-Obligation Estimate</h2>
          <p className="mt-5 text-[17px] text-[#555555] leading-[1.75]">
            Tell us about your property, your cleaning needs, and your preferred schedule, and we will provide a clear,
            professional proposal with no hidden fees.
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
            <a href={SITE.phoneHref} className="flex items-center justify-center gap-2 bg-white border border-[#D6DDD9] rounded-[10px] py-4 text-[14.5px] font-medium hover:border-[#2D6A4F] transition-colors">
              <Phone size={16} className="text-[#2D6A4F]" /> Call Us
            </a>
            <a href={SITE.emailHref} className="flex items-center justify-center gap-2 bg-white border border-[#D6DDD9] rounded-[10px] py-4 text-[14.5px] font-medium hover:border-[#2D6A4F] transition-colors">
              <Mail size={16} className="text-[#2D6A4F]" /> Email Us
            </a>
            <Link to="/contact" className="flex items-center justify-center gap-2 bg-[#00C17C] text-white rounded-[10px] py-4 text-[14.5px] font-semibold hover:bg-[#00A867] transition-colors">
              <ClipboardEdit size={16} /> Request Online
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
